import React, { useCallback, useEffect, useState } from 'react';
import appointmentService from '../../services/appointmentService';
import { Shell } from './AppointmentActionModals';
import DoctorSchedulePanel from './DoctorSchedulePanel';
import { describeSlotError } from './SlotPicker';
import {
  WEEKDAYS, addDaysIso, dateOnly, formatDateLabel, parseClosedWeekdays, serializeClosedWeekdays, todayIso,
} from '../../utils/appointmentLogic';

const errorText = (err, fallback) => err.response?.data?.message || describeSlotError(err) || fallback;

/** Opening hours and rules of one centre. */
const RulesTab = ({ centreId, onSaved, setBusy, busy }) => {
  const [cfg, setCfg] = useState(null);
  const [closed, setClosed] = useState(new Set());
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setCfg(null);
    appointmentService.getConfig(centreId)
      .then((res) => { if (!cancelled && res.success) { setCfg(res.data); setClosed(parseClosedWeekdays(res.data.closedWeekdays)); } })
      .catch((err) => { if (!cancelled) setError(errorText(err, 'Could not load the settings.')); });
    return () => { cancelled = true; };
  }, [centreId]);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const toggleDay = (d) => setClosed((s) => { const n = new Set(s); if (n.has(d)) n.delete(d); else n.add(d); return n; });

  const save = async () => {
    setBusy(true); setError('');
    try {
      const res = await appointmentService.saveConfig({
        centreId: Number(centreId), slotMinutes: Number(cfg.slotMinutes), openTime: cfg.openTime, closeTime: cfg.closeTime,
        maxPerSlot: Number(cfg.maxPerSlot), advanceDays: Number(cfg.advanceDays), closedWeekdays: serializeClosedWeekdays(closed) || null,
        autoNoShowMinutes: Number(cfg.autoNoShowMinutes) || 0, slipInstructions: cfg.slipInstructions?.trim() || null,
      });
      if (res.success) onSaved(); else setError(res.message || 'Could not save.');
    } catch (err) {
      setError(errorText(err, 'Could not save.'));
    } finally { setBusy(false); }
  };

  if (!cfg) return error ? <p className="apt-error-text" role="alert">{error}</p> : <p className="apt-hint">Loading…</p>;
  return (
    <>
      <div className="apt-grid">
        {!cfg.isConfigured && <p className="apt-hint apt-span-6">This centre uses the default settings until you save.</p>}
        <div className="apt-field apt-span-3">
          <label className="apt-label" htmlFor="apt-cfg-open">Opens</label>
          <input id="apt-cfg-open" type="time" className="apt-input" value={cfg.openTime} onChange={(e) => set({ openTime: e.target.value })} />
        </div>
        <div className="apt-field apt-span-3">
          <label className="apt-label" htmlFor="apt-cfg-close">Closes</label>
          <input id="apt-cfg-close" type="time" className="apt-input" value={cfg.closeTime} onChange={(e) => set({ closeTime: e.target.value })} />
        </div>
        <div className="apt-field apt-span-2">
          <label className="apt-label" htmlFor="apt-cfg-slot">Slot length (min)</label>
          <input id="apt-cfg-slot" type="number" min="5" max="240" className="apt-input" value={cfg.slotMinutes} onChange={(e) => set({ slotMinutes: e.target.value })} />
        </div>
        <div className="apt-field apt-span-2">
          <label className="apt-label" htmlFor="apt-cfg-cap">Patients per slot</label>
          <input id="apt-cfg-cap" type="number" min="1" max="100" className="apt-input" value={cfg.maxPerSlot} onChange={(e) => set({ maxPerSlot: e.target.value })} />
        </div>
        <div className="apt-field apt-span-2">
          <label className="apt-label" htmlFor="apt-cfg-adv">Book ahead (days)</label>
          <input id="apt-cfg-adv" type="number" min="0" max="365" className="apt-input" value={cfg.advanceDays} onChange={(e) => set({ advanceDays: e.target.value })} />
        </div>
        <div className="apt-field apt-span-6">
          <span className="apt-label">Closed on</span>
          <div className="apt-days">
            {WEEKDAYS.map((d) => (
              <label key={d.value} className={`apt-day ${closed.has(d.value) ? 'on' : ''}`}>
                <input type="checkbox" checked={closed.has(d.value)} onChange={() => toggleDay(d.value)} /> {d.label}
              </label>
            ))}
          </div>
        </div>
        <div className="apt-field apt-span-3">
          <label className="apt-label" htmlFor="apt-cfg-noshow">Mark as no-show automatically after (minutes)</label>
          <input id="apt-cfg-noshow" type="number" min="0" max="1440" className="apt-input" value={cfg.autoNoShowMinutes ?? 0} onChange={(e) => set({ autoNoShowMinutes: e.target.value })} />
          <span className="apt-hint">0 = never. Counted from the end of the slot; a patient who has arrived is never marked.</span>
        </div>
        <div className="apt-field apt-span-6">
          <label className="apt-label" htmlFor="apt-cfg-slip">Printed on the appointment slip</label>
          <textarea id="apt-cfg-slip" className="apt-input" rows={3} maxLength={500} value={cfg.slipInstructions || ''} onChange={(e) => set({ slipInstructions: e.target.value })}
            placeholder="For example: Fasting for 8 to 10 hours. Bring the doctor's prescription and an ID." />
        </div>
      </div>
      {error && <p className="apt-error-text" role="alert">{error}</p>}
      <div className="apt-modal-actions"><button type="button" className="apt-btn apt-btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</button></div>
    </>
  );
};

/** Specific closed dates: a festival, a maintenance day. A holiday set for all centres is for the Administrator. */
const HolidaysTab = ({ centreId, isAdmin }) => {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [allCentres, setAllCentres] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await appointmentService.listHolidays(centreId, todayIso(), addDaysIso(todayIso(), 365));
      if (res.success) setItems(res.data || []);
    } catch (err) { setError(errorText(err, 'Could not load the holidays.')); }
  }, [centreId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!date || !reason.trim()) { setError('Choose the date and give a reason.'); return; }
    setBusy(true); setError('');
    try {
      const res = await appointmentService.addHoliday({ centreId: allCentres ? 0 : Number(centreId), holidayDate: date, reason: reason.trim() });
      if (res.success) { setDate(''); setReason(''); await load(); } else setError(res.message || 'Could not add the holiday.');
    } catch (err) { setError(errorText(err, 'Could not add the holiday.')); } finally { setBusy(false); }
  };

  const remove = async (h) => {
    setBusy(true); setError('');
    try { await appointmentService.deleteHoliday(h.holidayId); await load(); }
    catch (err) { setError(errorText(err, 'Could not remove the holiday.')); } finally { setBusy(false); }
  };

  return (
    <>
      <p className="apt-hint">Booking is closed on these dates. Appointments already booked for a date are not cancelled; reschedule them from the list.</p>
      <div className="apt-inline-form">
        <input type="date" className="apt-input apt-input-sm" aria-label="Holiday date" min={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} />
        <input className="apt-input" aria-label="Reason" placeholder="Reason, for example Diwali" maxLength={150} value={reason} onChange={(e) => setReason(e.target.value)} />
        {isAdmin && <label className="apt-check"><input type="checkbox" checked={allCentres} onChange={(e) => setAllCentres(e.target.checked)} /> All centres</label>}
        <button type="button" className="apt-btn apt-btn-primary apt-btn-sm" onClick={add} disabled={busy}>Add holiday</button>
      </div>
      {error && <p className="apt-error-text" role="alert">{error}</p>}
      <ul className="apt-plain-list">
        {items.length === 0 && <li className="apt-muted">No holidays in the next year.</li>}
        {items.map((h) => (
          <li key={h.holidayId}>
            <span><strong>{formatDateLabel(h.holidayDate)}</strong> · {h.reason} {h.centreId === 0 && <em className="apt-muted">(all centres)</em>}</span>
            {(isAdmin || h.centreId !== 0) && <button type="button" className="apt-btn apt-btn-sm apt-btn-danger" onClick={() => remove(h)} disabled={busy} aria-label={`Remove ${dateOnly(h.holidayDate)}`}>Remove</button>}
          </li>
        ))}
      </ul>
    </>
  );
};

/** Places per slot for one department, below the centre's total (for example one MRI machine = 1). */
const DepartmentsTab = ({ centreId, departments }) => {
  const [items, setItems] = useState([]);
  const [departmentId, setDepartmentId] = useState('');
  const [places, setPlaces] = useState(1);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await appointmentService.listDepartmentCapacity(centreId);
      if (res.success) setItems(res.data || []);
    } catch (err) { setError(errorText(err, 'Could not load the department limits.')); }
  }, [centreId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!departmentId) { setError('Choose a department.'); return; }
    setBusy(true); setError('');
    try {
      const res = await appointmentService.saveDepartmentCapacity({ centreId: Number(centreId), departmentId: Number(departmentId), maxPerSlot: Number(places) });
      if (res.success) { setDepartmentId(''); await load(); } else setError(res.message || 'Could not save.');
    } catch (err) { setError(errorText(err, 'Could not save.')); } finally { setBusy(false); }
  };

  const remove = async (c) => {
    setBusy(true); setError('');
    try { await appointmentService.deleteDepartmentCapacity(centreId, c.departmentId); await load(); }
    catch (err) { setError(errorText(err, 'Could not remove.')); } finally { setBusy(false); }
  };

  return (
    <>
      <p className="apt-hint">A department with a limit never takes more patients per slot than this, even if the centre has room. Departments without a limit use the centre total.</p>
      <div className="apt-inline-form">
        <select className="apt-input" aria-label="Department" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
          <option value="">Choose department</option>
          {(departments || []).map((d) => <option key={d.departmentId} value={d.departmentId}>{d.name}</option>)}
        </select>
        <label className="apt-inline-label">Places per slot
          <input type="number" min="1" max="100" className="apt-input apt-input-sm" aria-label="Places per slot" value={places} onChange={(e) => setPlaces(e.target.value)} style={{ width: 70 }} />
        </label>
        <button type="button" className="apt-btn apt-btn-primary apt-btn-sm" onClick={save} disabled={busy}>Save limit</button>
      </div>
      {error && <p className="apt-error-text" role="alert">{error}</p>}
      <ul className="apt-plain-list">
        {items.length === 0 && <li className="apt-muted">No department limits set.</li>}
        {items.map((c) => (
          <li key={c.departmentId}>
            <span><strong>{c.departmentName || `Department ${c.departmentId}`}</strong> · {c.maxPerSlot} per slot</span>
            <button type="button" className="apt-btn apt-btn-sm apt-btn-danger" onClick={() => remove(c)} disabled={busy} aria-label={`Remove limit of ${c.departmentName}`}>Remove</button>
          </li>
        ))}
      </ul>
    </>
  );
};

export const SettingsModal = ({ centreId, centres, departments, doctors, isAdmin, onClose, onSaved, onDoctorsChanged }) => {
  const [selectedCentre, setSelectedCentre] = useState(centreId);
  const [tab, setTab] = useState('rules');
  const [busy, setBusy] = useState(false);

  return (
    <Shell title="Appointment settings" onClose={onClose} busy={busy} wide footer={<button type="button" className="apt-btn" onClick={onClose} disabled={busy}>Close</button>}>
      {isAdmin && centres?.length > 0 && (
        <div className="apt-field">
          <label className="apt-label" htmlFor="apt-cfg-centre">Centre</label>
          <select id="apt-cfg-centre" className="apt-input" value={selectedCentre} onChange={(e) => setSelectedCentre(e.target.value)}>
            {centres.map((c) => <option key={c.centreId} value={c.centreId}>{c.centreName}</option>)}
          </select>
        </div>
      )}
      <div className="apt-views" role="tablist" aria-label="Settings sections">
        {[['rules', 'Hours and rules'], ['holidays', 'Holidays'], ['departments', 'Department limits'], ['doctors', 'Doctors']].map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>
      {tab === 'rules' && <RulesTab key={selectedCentre} centreId={selectedCentre} onSaved={onSaved} busy={busy} setBusy={setBusy} />}
      {tab === 'holidays' && <HolidaysTab key={selectedCentre} centreId={selectedCentre} isAdmin={isAdmin} />}
      {tab === 'departments' && <DepartmentsTab key={selectedCentre} centreId={selectedCentre} departments={departments} />}
      {tab === 'doctors' && <DoctorSchedulePanel key={selectedCentre} centreId={selectedCentre} doctors={doctors} onChanged={onDoctorsChanged} />}
    </Shell>
  );
};

export default SettingsModal;
